import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { mirrorAdoptApi } from '@/api/mirrorAdopt';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Pill } from '@/components/ui/Pill';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { useIsMobile } from '@/hooks/useIsMobile';
import { apiErrorCode } from '@/lib/apiErrorCode';
import {
  adoptErrorPlaces,
  adoptErrorReport,
  adoptErrorRows,
  adoptRows,
  canApplyAdopt,
  mirrorAdoptErrorText,
  normalizeAdoptReport,
  type MirrorAdoptReport,
  type MirrorAdoptRollbackResult,
} from '@/lib/mirrorAdopt';

/** Сколько строк списков («не тронуто», блокеры, названия) показывать; остальное — числом. */
const SHOWN = 20;

/**
 * «Принять содержимое потока»: курс-новичок подстраивается под курс потока (договор `mirror-adopt.md`).
 * Выбор потока и образца → «Показать, что изменится» (предпросмотр — настоящий проход, откатанный сервером)
 * → «Применить» (только владелец, подтверждение с названием курса) → итог.
 *
 * ⚠️ ПРИМЕНЕНИЕ — ТОЛЬКО ПО `expect` ПРЕДПРОСМОТРА. Изменилось что-то между предпросмотром и нажатием —
 * сервер отвечает `409 adopt_plan_changed` со свежим отчётом; окно показывает его вместо старого.
 */
export function MirrorAdoptDialog({
  open,
  onClose,
  marathonId,
  title,
  subtype,
  subtypeFixed,
  subtypes,
  editable,
}: {
  open: boolean;
  onClose: () => void;
  marathonId: string;
  title: string;
  /** Поток по умолчанию: у курса в потоке — его собственный. */
  subtype: string;
  /** Курс уже в потоке — другой поток выбрать нельзя (сервер ответит 400). */
  subtypeFixed: boolean;
  subtypes: string[];
  editable: boolean;
}) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const ask = useConfirm();
  const qc = useQueryClient();
  const [flow, setFlow] = useState(subtype);
  const [source, setSource] = useState('');
  const [report, setReport] = useState<MirrorAdoptReport | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState<MirrorAdoptReport | null>(null);

  const reset = () => {
    setReport(null);
    setError(null);
  };

  const preview = useMutation({
    mutationFn: () => mirrorAdoptApi.preview(marathonId, flow, source),
    onMutate: reset,
    // Нормализуем и здесь, а не только в API: окно не должно падать из-за одного поля ответа (стенд 29.09.2026).
    onSuccess: (r) => setReport(normalizeAdoptReport(r)),
    onError: (e) => {
      setError(e);
      setReport(adoptErrorReport(e));
    },
  });

  const apply = useMutation({
    mutationFn: (expect: string) => mirrorAdoptApi.apply(marathonId, flow, source, expect, title),
    onSuccess: (r) => {
      setDone(normalizeAdoptReport(r));
      setError(null);
      qc.invalidateQueries({ queryKey: ['admin', 'marathon', marathonId] });
      qc.invalidateQueries({ queryKey: ['admin', 'marathon', marathonId, 'settings'] });
      qc.invalidateQueries({ queryKey: ['admin', 'mirror-adopt', marathonId] });
    },
    onError: (e) => {
      setError(e);
      const fresh = adoptErrorReport(e);
      if (fresh) setReport(fresh);
    },
  });

  const code = error ? apiErrorCode(error) : undefined;
  const places = adoptErrorPlaces(error);
  const shown = done ?? report;
  const rows = shown ? adoptRows(shown) : [];
  const removed = shown?.students_see.published_removed;
  const busy = preview.isPending || apply.isPending;

  // Кнопки: на телефоне — внизу окна во весь экран (§5.3, правило 3), на компьютере — под отчётом, как было.
  const buttons = (
    <div className={isMobile ? 'grid grid-cols-3 gap-2' : 'flex flex-wrap justify-end gap-2 pt-1'}>
      <Button variant="ghost" onClick={onClose} disabled={apply.isPending}>
        {done ? t('mirrorAdopt.close') : t('common.cancel')}
      </Button>
      {!done && (
        <Button variant="secondary" onClick={() => preview.mutate()} loading={preview.isPending} disabled={!flow || busy}>
          {t('mirrorAdopt.preview')}
        </Button>
      )}
      {!done && editable && (
        <Button
          variant="danger"
          loading={apply.isPending}
          disabled={!canApplyAdopt(report, editable, title) || (error != null && code !== 'adopt_plan_changed') || busy}
          onClick={async () => {
            if (!report?.expect) return;
            const ok = await ask({
              title: t('mirrorAdopt.confirmTitle'),
              message: t(report.joined ? 'mirrorAdopt.confirmJoin' : 'mirrorAdopt.confirm', {
                title,
                subtype: flow,
                source: report.source.title,
              }),
              confirmLabel: t('mirrorAdopt.apply'),
              danger: true,
            });
            if (ok) apply.mutate(report.expect);
          }}
        >
          {t('mirrorAdopt.apply')}
        </Button>
      )}
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('mirrorAdopt.title')}
      size="lg"
      phoneFull
      footer={isMobile ? buttons : undefined}
    >
      <div className="space-y-4 text-sm">
        <p className="text-on-surface-variant">{t('mirrorAdopt.intro')}</p>

        {!done && (
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              {t('mirrorAdopt.flow')}
              <select
                value={flow}
                disabled={subtypeFixed || busy}
                onChange={(e) => {
                  setFlow(e.target.value);
                  setSource('');
                  reset();
                }}
                className="mt-1 w-full rounded-lg border border-outline bg-surface px-3 py-2"
              >
                {!subtypeFixed && <option value="">{t('mirrorAdopt.flowPick')}</option>}
                {[...new Set([subtype, ...subtypes].filter(Boolean))].map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              {t('mirrorAdopt.source')}
              <select
                value={source}
                disabled={busy || !report?.sources?.length}
                onChange={(e) => {
                  setSource(e.target.value);
                  reset();
                }}
                className="mt-1 w-full rounded-lg border border-outline bg-surface px-3 py-2"
              >
                <option value="">{t('mirrorAdopt.sourceAuto')}</option>
                {(report?.sources ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {t('mirrorAdopt.sourceOption', { title: s.title, n: s.students })}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}

        {done && <p className="text-success">{t('mirrorAdopt.done', { title })}</p>}

        {shown && (
          <div className="space-y-3">
            <p className="text-on-surface-variant">
              {t('mirrorAdopt.sourceUsed', { title: shown.source.title, n: shown.source.students })}
              {shown.joined && ` · ${t('mirrorAdopt.joins', { subtype: shown.subtype })}`}
            </p>

            {rows.length === 0 ? (
              <p className="text-success">{t('mirrorAdopt.nothing')}</p>
            ) : isMobile ? (
              // Телефон (правило 9): таблица в 480 px уезжала вбок — строкой на уровень, числа с подписями столбцов.
              <ul className="divide-y divide-outline-variant/40" data-mirror-adopt-rows-phone>
                {rows.map((r) => (
                  <li key={r.level} className="py-2">
                    <p className="font-medium">{t(`mirrorAdopt.level_${r.level}`)}</p>
                    <p className="text-xs tabular-nums text-on-surface-variant">
                      {t('mirrorAdopt.colTake')} {r.take} · {t('mirrorAdopt.colDrop')} {r.drop} · {t('mirrorAdopt.colKept')}{' '}
                      {r.kept} · {t('mirrorAdopt.colAdded')} {r.added}
                    </p>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[480px] text-left">
                  <thead className="text-xs uppercase tracking-label text-on-surface-variant">
                    <tr>
                      <th className="py-1.5 pr-3">{t('mirrorAdopt.colLevel')}</th>
                      <th className="py-1.5 pr-3 text-right">{t('mirrorAdopt.colTake')}</th>
                      <th className="py-1.5 pr-3 text-right">{t('mirrorAdopt.colDrop')}</th>
                      <th className="py-1.5 pr-3 text-right">{t('mirrorAdopt.colKept')}</th>
                      <th className="py-1.5 pr-3 text-right">{t('mirrorAdopt.colAdded')}</th>
                    </tr>
                  </thead>
                  <tbody className="tabular-nums">
                    {rows.map((r) => (
                      <tr key={r.level} className="border-t border-outline-variant/40">
                        <td className="py-1.5 pr-3">{t(`mirrorAdopt.level_${r.level}`)}</td>
                        <td className="py-1.5 pr-3 text-right">{r.take}</td>
                        <td className="py-1.5 pr-3 text-right">{r.drop}</td>
                        <td className="py-1.5 pr-3 text-right">{r.kept}</td>
                        <td className="py-1.5 pr-3 text-right">{r.added}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="rounded-card bg-surface-container-highest p-3">
              <p className="font-medium">{t('mirrorAdopt.studentsSee')}</p>
              <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs">
                <li>
                  {t('mirrorAdopt.seeRemoved', {
                    lessons: removed?.lesson ?? 0,
                    sections: removed?.section ?? 0,
                    blocks: removed?.block ?? 0,
                  })}
                </li>
                {(removed?.titles ?? []).slice(0, SHOWN).map((x) => (
                  <li key={x} className="text-on-surface-variant">
                    {x}
                  </li>
                ))}
                <li>{t('mirrorAdopt.seeAddedBlocks', { n: shown.students_see.published_added_blocks })}</li>
                <li>{t('mirrorAdopt.seeDraftLessons', { n: shown.students_see.draft_lessons_added })}</li>
                <li>{t('mirrorAdopt.seeNeighbors')}</li>
              </ul>
            </div>

            {shown.kept.length > 0 && (
              <div className="rounded-card bg-surface-container-highest p-3">
                <p className="font-medium">{t('mirrorAdopt.keptTitle', { n: shown.kept.length })}</p>
                <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs">
                  {shown.kept.slice(0, SHOWN).map((k) => (
                    <li key={k.block_id}>
                      {t('mirrorAdopt.keptRow', {
                        lesson: k.lesson_title,
                        section: k.section_title,
                        type: k.type,
                        answers: k.answers,
                        students: k.students,
                      })}
                    </li>
                  ))}
                </ul>
                <p className="mt-1 text-xs text-on-surface-variant">{t('mirrorAdopt.keptHint')}</p>
              </div>
            )}

            {shown.blockers.length > 0 && (
              <div className="rounded-card bg-error/5 p-3 text-error">
                <p className="font-medium">{t('mirrorAdopt.blockersTitle', { n: shown.blockers.length })}</p>
                <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs">
                  {shown.blockers.slice(0, SHOWN).map((b) => (
                    <li key={b.id}>
                      {t(`mirrorAdopt.blocker_${b.reason}`, {
                        level: t(`mirrorAdopt.level_${b.level}`),
                        lesson: b.lesson_title,
                        title: b.title,
                        n: b.traces,
                      })}
                    </li>
                  ))}
                </ul>
                <p className="mt-1 text-xs">{t('mirrorAdopt.blockersHint')}</p>
              </div>
            )}
          </div>
        )}

        {error != null && code !== 'adopt_needs_decision' && (
          <div className="rounded-card bg-error/5 p-3 text-error">
            <p>{mirrorAdoptErrorText(error, t)}</p>
            {places.slice(0, SHOWN).map((p) => (
              <p key={`${p.level}-${p.key}`} className="mt-1 text-xs">
                {t(`mirrorAdopt.level_${p.level}`)} · {p.lesson_title ?? p.key.slice(0, 8)}
              </p>
            ))}
          </div>
        )}

        {!editable && report && !done && <p className="text-on-surface-variant">{t('mirrorAdopt.ownerOnlyHint')}</p>}

        {!isMobile && buttons}
      </div>
    </Modal>
  );
}

/**
 * Журнал подстроек курса с «Откатить» (только владелец, только последняя неотменённая — `can_rollback`).
 * Пустой журнал не рисуется: у большинства курсов подстроек нет.
 */
export function MirrorAdoptJournal({ marathonId, title, editable }: { marathonId: string; title: string; editable: boolean }) {
  const { t } = useTranslation();
  const ask = useConfirm();
  const qc = useQueryClient();
  const [result, setResult] = useState<MirrorAdoptRollbackResult | null>(null);

  const journal = useQuery({
    queryKey: ['admin', 'mirror-adopt', marathonId],
    queryFn: () => mirrorAdoptApi.journal(marathonId),
  });
  const rollback = useMutation({
    mutationFn: (runId: string) => mirrorAdoptApi.rollback(marathonId, runId, title),
    onSuccess: (r) => {
      setResult(r);
      qc.invalidateQueries({ queryKey: ['admin', 'mirror-adopt', marathonId] });
      qc.invalidateQueries({ queryKey: ['admin', 'marathon', marathonId] });
      qc.invalidateQueries({ queryKey: ['admin', 'marathon', marathonId, 'settings'] });
    },
  });

  const runs = journal.data ?? [];
  if (runs.length === 0 && !journal.error) return null;
  const blocking = adoptErrorRows(rollback.error);

  return (
    <div className="mt-4 border-t border-outline-variant/40 pt-3">
      <p className="mb-2 text-[13px] font-semibold text-on-surface">{t('mirrorAdopt.journalTitle')}</p>
      {journal.error ? (
        <p className="text-sm text-error">{mirrorAdoptErrorText(journal.error, t)}</p>
      ) : (
        <ul className="space-y-2 text-sm">
          {runs.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-card bg-surface-container-highest p-3">
              <div>
                <p>
                  {r.subtype}
                  {r.joined && ` · ${t('mirrorAdopt.journalJoined')}`}
                </p>
                <p className="text-xs text-on-surface-variant">{new Date(r.created_at).toLocaleString('ru-RU')}</p>
              </div>
              <div className="flex items-center gap-2">
                {r.status === 'rolled_back' ? (
                  <Pill>{t('mirrorAdopt.rolledBack')}</Pill>
                ) : (
                  <Pill variant="success">{t('mirrorAdopt.statusDone')}</Pill>
                )}
                {editable && r.can_rollback && (
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={rollback.isPending && rollback.variables === r.id}
                    onClick={async () => {
                      const ok = await ask({
                        title: t('mirrorAdopt.confirmRollbackTitle'),
                        message: t('mirrorAdopt.confirmRollback', { title }),
                        confirmLabel: t('mirrorAdopt.rollback'),
                        danger: true,
                      });
                      if (ok) {
                        setResult(null);
                        rollback.mutate(r.id);
                      }
                    }}
                  >
                    {t('mirrorAdopt.rollback')}
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {result && (
        <p className="mt-2 text-sm text-success">
          {t(result.left_subtype ? 'mirrorAdopt.rollbackDoneLeft' : 'mirrorAdopt.rollbackDone')}
        </p>
      )}
      {rollback.error != null && (
        <div className="mt-2 text-sm text-error">
          <p>{mirrorAdoptErrorText(rollback.error, t)}</p>
          {blocking.slice(0, SHOWN).map((b) => (
            <p key={`${b.level}-${b.id}`} className="text-xs">
              {b.level === 'marathon' ? t('mirrorAdopt.rowSubtype') : `${t(`mirrorAdopt.level_${b.level}`)} · ${b.title}`}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
